import { useMutation } from "@apollo/client";
import { Fragment, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { MUTATION_DUPLICATE_APPLICATION } from "api/mutations/application";

import { applicationStepOne } from "pages/ApplicationForm/formSteps";
import {
  canContinueApplication,
  isCaseComplete,
} from "pages/ApplicationForm/utils";
import ReopenApplicationDialog from "./ReopenApplicationDialog";
import { getCaseDebtConsolidationUrl, getCaseDocumentsUrl } from "routes/links";
import useApplicationStore from "store/Application";
import { Case } from "ts/interfaces/common/case";
import { renderDate } from "utils/helpers";

interface Application {
  downloadUrl?: string;
  reference: string;
  createdOn: string;
  applicationDate?: string;
  status: string;
  uuid: string;
  isLatestSubmission?: boolean;
  hasDebtsToSettle?: boolean;
}

interface ApplicationsProps {
  caseDetail: Case;
  applications: Application[];
}

const ApplicationActions: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <tr>
      <td
        colSpan={3}
        className="whitespace-nowrap py-4 pl-4 pr-3 text-right text-sm font-medium text-gray-900 sm:pl-6"
      >
        {children}
      </td>
    </tr>
  );
};

const Applications: React.FC<ApplicationsProps> = ({
  caseDetail,
  applications,
}) => {
  const navigate = useNavigate();

  const [isReopenDialogOpen, setIsReopenDialogOpen] = useState(false);

  const { resetApplication } = useApplicationStore();

  const setApplicationToContinue = (applicationID: string) => {
    resetApplication();
    navigate(applicationStepOne(applicationID));
  };

  const renderSubmittedDate = (applicationDate: string | undefined) => {
    return applicationDate ? renderDate(applicationDate) : "-";
  };

  const hasOpenApplications = applications.some((app) =>
    canContinueApplication(app.status),
  );

  const [
    duplicateGivenApplication,
    {
      data: duplicateApplicationData,
      loading: duplicateApplicationLoading,
      error: duplicateApplicationError,
    },
  ] = useMutation(MUTATION_DUPLICATE_APPLICATION);

  const handleDuplicateApplication = async (applicationID: string) => {
    try {
      await duplicateGivenApplication({
        variables: {
          applicationId: applicationID,
        },
      });
    } catch (error) {}
  };

  useEffect(() => {
    if (duplicateApplicationData) {
      navigate(
        applicationStepOne(`${duplicateApplicationData.data.application.uuid}`),
      );
    }
  }, [duplicateApplicationData, navigate]);

  const renderApplicationActions = (app: Application) => {
    if (isCaseComplete(caseDetail.status)) {
      return <></>;
    }

    if (canContinueApplication(app.status)) {
      return (
        <ApplicationActions>
          <button
            onClick={() => setApplicationToContinue(app.uuid)}
            className="link font-medium underline"
          >
            Continue
          </button>
        </ApplicationActions>
      );
    }

    if (app?.isLatestSubmission) {
      return (
        <ApplicationActions>
          <a download href={app?.downloadUrl} className="link underline">
            Download submission
          </a>
          <span className="px-2 text-gray-300">|</span>
          <Link
            to={getCaseDocumentsUrl(caseDetail.uuid)}
            className="link underline"
          >
            Supporting documents
          </Link>
          <span className="px-2 text-gray-300">|</span>
          <Link
            to={getCaseDebtConsolidationUrl(caseDetail.uuid, app.uuid)}
            className="link underline"
          >
            Debt Consolidation Form
          </Link>
          {!hasOpenApplications && (
            <>
              <span className="px-2 text-gray-300">|</span>
              <button
                onClick={() => setIsReopenDialogOpen(true)}
                className="link font-medium underline"
                disabled={duplicateApplicationLoading}
              >
                Re-open Application
              </button>
              {duplicateApplicationError && (
                <div className="mb-4 rounded bg-red-50 p-3 text-red-700">
                  Got an unexpected error, please contact or try again later.
                </div>
              )}
              <ReopenApplicationDialog
                open={isReopenDialogOpen}
                onClose={() => setIsReopenDialogOpen(false)}
                onConfirm={() => handleDuplicateApplication(app.uuid)}
                loading={duplicateApplicationLoading}
              />
            </>
          )}
        </ApplicationActions>
      );
    }

    return <></>;
  };

  return (
    <div className="overflow-hidden rounded-lg shadow ring-1 ring-black ring-opacity-5">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            >
              Reference
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Date started
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Date submitted
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {applications.length ? (
            applications.map((app, i) => (
              <Fragment key={i}>
                <tr>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                    {app.reference}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                    {renderDate(app.createdOn)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-600">
                    {renderSubmittedDate(app?.applicationDate)}
                  </td>
                </tr>
                {renderApplicationActions(app)}
              </Fragment>
            ))
          ) : (
            <tr>
              <td
                colSpan={3}
                className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
              >
                -
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Applications;
